import {
    CHECK_IN_PROGRESS,
    REJECT_ALL_DISPLAY_CATEGORIES,
    SCAN_ERROR_CODES,
    SCAN_REQUIREMENTS,
    SCAN_REQUIREMENT_MAPPING,
} from '/b2b/domains/constants';
import { formatPaging, makeResultsFormatter } from '/b2b/common/services/helpers';

import { camelCaseObjectKeys } from '/b2b/common/helpers/Format';
import kebabCase from 'lodash/kebabCase';
import lowerCase from 'lodash/lowerCase';
import { v4 as uuidv4 } from 'uuid';

const GPC_SUBCHECKS = [
    'gpcJson',
    'signalVisual',
    'trackerAnalysis', // v2 (cookies + localStorage); replaces legacy cookieAnalysis
    'signalAnalysis', // v2 (network/consent signals); replaces legacy requestAnalysis
    'cookieAnalysis', // legacy v1 — only present in historical reports
    'requestAnalysis',
    'usPrivacyString',
    'gppString',
    'googleConsent',
];

// When a prerequisite check fails, the scan backend skips the checks that depend on it
// and omits them from the response entirely. We re-create those skipped checks here so
// they still appear in the UI (as failed, using the same failure label and suggestion as
// a normal failure) and the check count stays accurate, rather than silently
// disappearing. See CCM-509.
//
// NOTE: this map must stay aligned with scan-api's SKIPPED_CHECK_DEPENDENCIES
// (app/constants.py), which feeds the domain-list counts. This copy feeds the domain
// details view; if a check's dependencies change, update both or the count and details
// will diverge.
const SKIPPED_CHECK_DEPENDENCIES = {
    [SCAN_REQUIREMENTS.COOKIE_BANNER_PRESENCE]: [SCAN_REQUIREMENTS.COOKIE_BANNER_BUTTONS],
    [SCAN_REQUIREMENTS.PRIVACY_POLICY_PRESENCE]: [
        SCAN_REQUIREMENTS.PRIVACY_POLICY_FRESHNESS,
        SCAN_REQUIREMENTS.SUBJECT_RIGHTS_METHOD,
    ],
};

const formatJurisdictionData = resultsSummary =>
    Object.entries(resultsSummary || {}).reduce(
        (acc, [jurisdiction, results]) => ({
            ...acc,
            [kebabCase(lowerCase(jurisdiction))]: camelCaseObjectKeys(results),
        }),
        {}
    );

// Osano category ids (on the flat tracker rows and the trackerAnalysis by-category counts)
// are uppercase (e.g. MARKETING); the UI's category names are lowercase. ``uncategorized``
// (the analyzer's no-category default) maps onto the UI's ``unclassified`` bucket.
const normalizeCategory = value => {
    const name = typeof value === 'string' ? value.toLowerCase() : '';
    return name === 'uncategorized' ? 'unclassified' : name;
};

// The identity of a tracker domain on a flat v2 tracker row. `artifactMatch` is the analyzer's
// rule (a hostname or cookie-name pattern); rows that predate it, or that the analyzer could not
// attribute, fall back to the observed name. A row carrying neither is malformed and collapses
// into a single `unknown` bucket rather than each becoming its own domain.
//
// ALWAYS key tracker grouping and counting through this. The grids, the PDF template and the PDF
// normalizer each build the same domain groups, and a fallback order that differs between them
// puts two different numbers on the same panel — the discrepancy CCM-576 was filed for.
export const getTrackerKey = tracker => tracker.artifactMatch || tracker.artifactName || 'unknown';

// Count distinct tracker domains in a flat artifact list.
//
// `detail.trackers` is granular — one row per artifact (cookie key, request URL) — so a single
// tracker domain firing several requests contributes several rows. Counting rows gives a
// request-level number while the grids, the `remainingByCategory` fallback and the AI summary all
// describe domains. Headline counts are keyed the way the grids group their rows, so the number
// always matches what the user can count.
export const countDistinctTrackers = trackers => new Set(trackers.map(getTrackerKey)).size;

// Build the category -> tracker (grouped by artifact_match) -> artifact render tree the reject-all
// grid and PDF render, from the canonical v2 flat trackers (`[ArtifactMatch...]`). Each row carries
// `presentOnInitialLoad` (baseline) and `presentAfterRejectAll` (still present after the reject-all
// action), which map onto the UI's `presentBefore` / `presentAfter`. Categories are normalized to
// lowercase here so no consumer sees a raw uppercase id.
const formatFlatRejectAllTrackers = trackers => {
    const byCategory = {};
    (trackers || []).forEach(t => {
        const name = normalizeCategory(t.category) || 'unclassified';
        const matchKey = getTrackerKey(t);
        byCategory[name] = byCategory[name] || {};
        const group = (byCategory[name][matchKey] = byCategory[name][matchKey] || {
            domain: matchKey,
            vendor: t.trackerBusiness || null,
            trackers: [],
        });
        if (!group.vendor && t.trackerBusiness) group.vendor = t.trackerBusiness;
        group.trackers.push({
            key: t.artifactName,
            type: t.artifactKind,
            presentBefore: !!t.presentOnInitialLoad,
            presentAfter: !!t.presentAfterRejectAll,
        });
    });

    return {
        categories: REJECT_ALL_DISPLAY_CATEGORIES.filter(name => name in byCategory).map(name => {
            const trackers = Object.values(byCategory[name]).map(g => ({
                domain: g.domain,
                vendor: g.vendor,
                trackerCountBefore: g.trackers.filter(t => t.presentBefore).length,
                trackerCountAfter: g.trackers.filter(t => t.presentAfter).length,
                trackers: g.trackers,
            }));
            return {
                name,
                domainCountBefore: trackers.filter(t => t.trackerCountBefore > 0).length,
                domainCountAfter: trackers.filter(t => t.trackerCountAfter > 0).length,
                violationsCount: trackers.filter(t => t.trackerCountAfter > 0).length,
                trackers,
            };
        }),
    };
};

// Shared render helper: group the canonical flat trackers into the category tree. Exported for both
// the grid component and the PDF (both need the hierarchy). The grouping happens at render time
// because the flat list only ships on the lazily-hydrated S3 sidecar, not the summary payload.
export const buildRejectAllTree = trackers => formatFlatRejectAllTrackers(trackers);

// Derive the per-category before/after counts the UI renders from the v2 by-category maps. Returns
// `{ <lowercat>: { domainCountBefore, domainCountAfter, trackerCountBefore, trackerCountAfter } }` —
// the same numeric shape the old `categorySummaries` carried, so downstream totals/badges are
// unchanged. ALWAYS read the v2 by-category maps through this (never directly): it applies the
// uppercase->lowercase normalization, so consumers never see a raw category key.
export const selectRejectAllCategoryCounts = trackerAnalysis => {
    const removed = trackerAnalysis?.removedByCategory || {};
    const remaining = trackerAnalysis?.remainingByCategory || {};
    const counts = {};
    [...Object.keys(removed), ...Object.keys(remaining)].forEach(rawCat => {
        const name = normalizeCategory(rawCat);
        if (!name || name in counts) return;
        const removedCount = removed[rawCat] || 0;
        const remainingCount = remaining[rawCat] || 0;
        counts[name] = {
            // Distinct-tracker counts: "before" = removed + remaining (present on initial load),
            // "after" = remaining (still present after reject-all).
            domainCountBefore: removedCount + remainingCount,
            domainCountAfter: remainingCount,
            trackerCountBefore: removedCount + remainingCount,
            trackerCountAfter: remainingCount,
        };
    });
    return counts;
};

// Flatten a legacy v1 nested detail tree into the canonical flat ArtifactMatch[] the render helpers
// understand. Handles the raw v1 shape (`{ categories: { <name>: { domains: [...] } } }`), an
// already-formatted tree (`{ categories: [{ name, trackers: [...] }] }`), and a v2 flat list.
const flattenLegacyDetail = detail => {
    if (Array.isArray(detail?.trackers)) return detail.trackers;

    const categories = detail?.categories || {};
    const entries = Array.isArray(categories)
        ? categories.map(c => [c.name, c])
        : Object.entries(categories);

    const out = [];
    entries.forEach(([name, cat]) => {
        const category = normalizeCategory(name);
        (cat.domains || cat.trackers || []).forEach(domainEntry => {
            (domainEntry.trackers || []).forEach(t => {
                out.push({
                    // Case-insensitive: origins serve mixed-case content-type headers
                    // (`APPLICATION/JavaScript`).
                    artifactKind: /javascript$/i.test(t.type) ? 'script' : 'request',
                    artifactName: t.key,
                    artifactMatch: domainEntry.domain,
                    trackerBusiness: domainEntry.vendor || null,
                    category,
                    presentOnInitialLoad: !!t.presentBefore,
                    presentAfterRejectAll: !!t.presentAfter,
                });
            });
        });
    });
    return out;
};

// Deletable shim: lift a historical v1 reject-all report into the canonical v2 shape. v1 gave us
// per-category before/after distinct-tracker counts in `categorySummaries`; v2 expresses the same as
// `removedByCategory` (before - after) / `remainingByCategory` (after). We also flatten the v1 nested
// detail back to a flat ArtifactMatch list so the shared grouping helper rebuilds the tree
// identically for v1 and v2. Delete once v1 scans age out.
const adaptLegacyRejectAllToV2 = rawFields => {
    // Pull the legacy fields out so they don't linger on the canonical v2 envelope; fold them into
    // trackerAnalysis instead. Everything else (summary, suggestedAction, pageUrl, …) passes through.
    const { categorySummaries, detail, cmpDetected, cmpVendor, ...rest } = rawFields;

    // Error rows carry neither summaries nor detail — nothing to lift.
    if (!categorySummaries && !detail) return rawFields;

    // v1 categorySummaries keys are already the normalized UI category names, which is exactly the
    // (lowercased) key shape real v2 by-category maps have after camelCaseObjectKeys.
    const removedByCategory = {};
    const remainingByCategory = {};
    Object.entries(categorySummaries || {}).forEach(([category, s]) => {
        const before = s.trackerCountBefore || 0;
        const after = s.trackerCountAfter || 0;
        removedByCategory[category] = Math.max(before - after, 0);
        remainingByCategory[category] = after;
    });

    return {
        ...rest,
        trackerAnalysis: {
            removedByCategory,
            remainingByCategory,
            cmpDetected,
            cmpVendor,
            ...(detail ? { detail: { trackers: flattenLegacyDetail(detail) } } : {}),
        },
    };
};

// Normalize reject-all fields to the canonical v2 shape the UI consumes. v2 scans: lift the
// `trackerAnalysis` subcheck (incl. its raw flat `detail.trackers` when hydrated) to top-level
// `fields.trackerAnalysis`, leaving the flat->tree grouping to render time. v1 scans (historical):
// run the deletable shim that synthesizes the same v2-shaped trackerAnalysis.
const adaptRejectAllFields = rawFields => {
    const trackerAnalysisFields = rawFields.trackerAnalysis?.fields;

    if (!trackerAnalysisFields) {
        return adaptLegacyRejectAllToV2(rawFields);
    }

    return {
        ...rawFields,
        trackerAnalysis: trackerAnalysisFields,
    };
};

const adaptFirstLoadFields = rawFields => {
    const trackerAnalysisFields = rawFields.trackerAnalysis?.fields;
    if (!trackerAnalysisFields) return rawFields;
    return { ...rawFields, trackerAnalysis: trackerAnalysisFields };
};

// A warning is a non-blocking concern, so it does not count as a failure.
const NON_FAILING_STATUSES = new Set(['passed', 'warning']);

const isKnownCheck = result => {
    if (!result || typeof result !== 'object') {
        return false;
    }

    return result.status !== undefined || result.errorCode;
};

const formatScanResultsFromSummary = resultsSummary => {
    const requirementMap = {};

    // Group results by requirement across all jurisdictions
    Object.entries(resultsSummary || {}).forEach(([jurisdiction, results]) => {
        Object.entries(results)
            .filter(([, result]) => isKnownCheck(result))
            .sort(([a], [b]) => {
                const orderA = SCAN_REQUIREMENT_MAPPING[a]?.order || 0;
                const orderB = SCAN_REQUIREMENT_MAPPING[b]?.order || 0;

                return orderA - orderB;
            })
            .forEach(([requirement, result]) => {
                const scanRequirement = SCAN_REQUIREMENT_MAPPING[requirement];

                if (!scanRequirement) {
                    return;
                }

                const justifications = scanRequirement?.justifications?.[jurisdiction] || [];

                if (!requirementMap[requirement]) {
                    requirementMap[requirement] = {
                        requirement,
                        jurisdictions: {},
                    };
                }

                const isError = result.status === 'error' || Boolean(result.errorCode);

                const rawFields = result.fields || {};
                let fields;
                if (requirement === SCAN_REQUIREMENTS.REJECT_ALL) {
                    fields = adaptRejectAllFields(rawFields);
                } else if (requirement === SCAN_REQUIREMENTS.FIRST_LOAD) {
                    fields = adaptFirstLoadFields(rawFields);
                } else {
                    fields = rawFields;
                }

                // The backend reports checks it skipped (due to an unchanged page or a
                // failed prerequisite) with status 'skipped'; mark them failed and
                // surface them like the checks we synthesize below.
                const isSkipped = result.status === 'skipped';

                const isInProgress = result.status === CHECK_IN_PROGRESS;

                let status;
                if (isError) {
                    status = 'error';
                } else if (isSkipped) {
                    status = 'failed';
                } else if (isInProgress) {
                    status = CHECK_IN_PROGRESS;
                } else {
                    status = result.status;
                }

                // For GPC, re-derive the verdict from subchecks: if any subcheck
                // failed, the overall GPC check is failed regardless of its own status.
                if (
                    requirement === SCAN_REQUIREMENTS.GLOBAL_PRIVACY_CONTROL &&
                    !isError &&
                    !isSkipped &&
                    !isInProgress
                ) {
                    const presentSubchecks = GPC_SUBCHECKS.filter(key => rawFields[key]);
                    const allNonFailing =
                        presentSubchecks.length > 0 &&
                        presentSubchecks.every(key => rawFields[key]?.status !== 'failed');
                    status = allNonFailing ? 'passed' : 'failed';
                }

                requirementMap[requirement].jurisdictions[jurisdiction] = {
                    status,
                    fields,
                    errorCode: result.errorCode,
                    errorMessage: result.errorMessage,
                    ...(result.cacheHit && { cacheHit: true }),
                    ...(result.regimes && { regimes: result.regimes }),
                    requirement,
                    justifications,
                };
            });
    });

    // Re-create checks the backend skipped (and omitted) because a prerequisite check
    // failed, so they surface as failures instead of silently vanishing. (CCM-509)
    Object.entries(resultsSummary || {}).forEach(([jurisdiction, results]) => {
        Object.entries(SKIPPED_CHECK_DEPENDENCIES).forEach(([parent, dependents]) => {
            const parentResult = results[parent];
            const parentFailed =
                parentResult &&
                parentResult.status !== 'error' &&
                !NON_FAILING_STATUSES.has(parentResult.status);

            if (!parentFailed) {
                return;
            }

            dependents.forEach(dependent => {
                // Only synthesize when the dependent really is absent (i.e. was skipped).
                if (results[dependent] || requirementMap[dependent]?.jurisdictions[jurisdiction]) {
                    return;
                }

                if (!requirementMap[dependent]) {
                    requirementMap[dependent] = { requirement: dependent, jurisdictions: {} };
                }

                requirementMap[dependent].jurisdictions[jurisdiction] = {
                    status: 'failed',
                    fields: {},
                    requirement: dependent,
                    justifications: [],
                };
            });
        });
    });

    // Convert to array format with aggregated pass/fail logic, ordered by requirement.
    return Object.values(requirementMap)
        .map(item => {
            const jurisdictionResults = Object.values(item.jurisdictions);
            const hasError = jurisdictionResults.some(result => result.status === 'error');

            // Judged only on jurisdictions that have reported, so one still running cannot fail it.
            const inProgress = jurisdictionResults.some(
                result => result.status === CHECK_IN_PROGRESS
            );
            const reported = jurisdictionResults.filter(
                result => result.status !== CHECK_IN_PROGRESS
            );
            const allNonFailing =
                reported.length > 0 &&
                reported.every(result => NON_FAILING_STATUSES.has(result.status));

            const cacheHit = jurisdictionResults.every(result => result.cacheHit);

            let status;
            if (hasError) {
                status = 'error';
            } else if (inProgress) {
                status = CHECK_IN_PROGRESS;
            } else {
                status = allNonFailing ? 'passed' : 'failed';
            }

            return {
                id: uuidv4(),
                status,
                ...(cacheHit && { cacheHit: true }),
                ...item,
            };
        })
        .sort((a, b) => {
            const orderA = SCAN_REQUIREMENT_MAPPING[a.requirement]?.order || 0;
            const orderB = SCAN_REQUIREMENT_MAPPING[b.requirement]?.order || 0;

            return orderA - orderB;
        });
};

// The analyzer reports spider failures (e.g. S007 navigation failure) only in the
// scan-level error envelope, never inside the per-check results, so the GPC check
// alone reads as a generic failure. Merge the envelope onto the GPC check so the
// details panel can branch on the error code. (CCM-509)
const mergeScanErrorEnvelope = (resultsSummary, errorEnvelope) => {
    Object.entries(errorEnvelope || {}).forEach(([jurisdiction, envelope]) => {
        const gpcCheck = resultsSummary?.[jurisdiction]?.[SCAN_REQUIREMENTS.GLOBAL_PRIVACY_CONTROL];

        if (
            envelope?.code !== SCAN_ERROR_CODES.NAVIGATION_FAILED ||
            !gpcCheck ||
            NON_FAILING_STATUSES.has(gpcCheck.status) ||
            gpcCheck.errorCode
        ) {
            return;
        }

        gpcCheck.errorCode = envelope.code;
        gpcCheck.errorMessage = envelope.error;
        gpcCheck.status = 'error';
    });
};

export const formatDomainScan = scan => {
    const {
        error_message = '',
        error = error_message,
        results_summary = {},
        resultsSummary: summary = results_summary,
        ...remain
    } = scan || {};

    let parsedError;
    try {
        parsedError = JSON.parse(error);
    } catch (e) {
        parsedError = {};
    }

    const errorMessage = formatJurisdictionData(parsedError);
    const resultsSummary = formatJurisdictionData(summary);

    mergeScanErrorEnvelope(resultsSummary, errorMessage);

    const results = formatScanResultsFromSummary(resultsSummary);

    return {
        ...camelCaseObjectKeys(remain),
        errorMessage,
        resultsSummary,
        results,
    };
};

export const formatDomain = data => {
    const { orgs = [], scans = [], urls = [] } = data;
    const latestScan = scans?.[0] || {};

    return {
        ...data,
        latestScan: latestScan ? formatDomainScan(latestScan) : {},
        orgs: orgs
            ?.filter(org => org.name !== 'ALL')
            ?.map(org => ({
                ...org,
                label: org.name,
                value: org.orgId,
            })),
        scans: scans?.map(formatDomainScan),
        urls: urls?.map(url => ({
            ...url,
            label: url.url,
            value: url.urlId,
        })),
    };
};

export const formatDomainResults = makeResultsFormatter(formatDomain);

/**
 * Format domain summary response from /website-scan/api/v1/scan/compliance-check/sites/summary
 * into the format expected by the DomainScans dashboard widget
 */
export const formatDomainSummaryResults = response => {
    const { domains = [], paging = {} } = response || {};

    const formattedDomains = domains.map(domain => ({
        ...camelCaseObjectKeys(domain),
        domainId: domain.domain,
    }));

    const domainsWithFailures = formattedDomains.filter(
        domain => domain.failedChecksCount > 0 || domain.errorChecksCount > 0
    ).length;

    return {
        results: formattedDomains,
        domainsWithFailures,
        totalDomains: formattedDomains.length,
        ...formatPaging(paging),
    };
};

/**
 * Format domain list response from /website-scan/api/v1/scan/compliance-check/sites/list
 * into the format expected by useORMResults/DataGrid
 */
export const formatDomainListResults = response => {
    const { domains = [], paging = {}, totals } = response || {};

    const formattedDomains = domains.map(domain => ({
        ...camelCaseObjectKeys(domain),
    }));

    return {
        results: formattedDomains,
        totals,
        ...formatPaging(paging),
    };
};

export const formatDomainScans = (filters, domainScans) => ({
    results: (domainScans.items || []).map(formatDomainScan),
    paging: formatPaging({
        pageNumber: filters.skip / filters.limit + 1,
        pageSize: filters.limit,
        pages: Math.ceil(domainScans.total / filters.limit),
        total: domainScans.total || 0,
    }),
});
